import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Cookies from 'js-cookie';
import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { dispatchLogin } from '../redux/slice/auth/auth.operations';
import { LoginDTO } from '../redux/slice/auth/auth.types';
import { useAppDispatch } from '../redux/store';
import logger from '../services/logger';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const queryParams: URLSearchParams = new URLSearchParams(location.search);
  const organization: string | undefined =
    queryParams.get('organization') ?? Cookies.get('organization');
  const disablePersonalInfo: string | undefined =
    queryParams.get('disablePersonalInfo') ?? Cookies.get('disablePersonalInfo');
  const LoginSchema = yup
    .object({
      email: yup.string().email('Invalid email address').required(),
      password: yup.string().required(),
    })
    .required();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(LoginSchema),
  });

  interface IFormInput {
    email: string;
    password: string;
  }
  const handleLogin: SubmitHandler<IFormInput> = async (dto: LoginDTO): Promise<void> => {
    logger.debug('Submitted form to login.', dto);

    try {
      await dispatch(dispatchLogin(dto));
      toast.success('Welcome back!');
      navigate('/pre-employment/start');
    } catch (e) {
      logger.error('Failed to login.', e);
      toast.error('Incorrect login details.');
    }
  };

  useEffect(() => {
    // TODO: create utility function for this because it's used in multiple places
    if (organization) {
      Cookies.set('organization', organization, { path: '/' });
    }
  }, [organization]);

  useEffect(() => {
    if (disablePersonalInfo !== undefined && Cookies.get('disablePersonalInfo') === undefined) {
      Cookies.set('disablePersonalInfo', 'true');
    }
  }, [disablePersonalInfo]);

  return (
    <div className="flex items-center justify-center w-full">
      <div
        className={`mx-auto w-full max-w-lg bg-white-100 rounded-xl p-10 border border-black/10`}
      >
        <h2 className="text-center text-2xl font-bold leading-tight mb-4">
          Sign in to your account
        </h2>
        <p className="mb-8 text-center text-base text-black/60">
          Don&apos;t have any account?&nbsp;
          <Link
            to="/signup"
            className="text-purple-700 font-medium text-primary transition-all duration-200 hover:underline"
          >
            Sign Up
          </Link>
        </p>
        <div className="space-y-5">
          <Box
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(handleLogin)}
            component="form"
            sx={{
              '& > :not(style)': { m: 1, width: '42ch' },
            }}
            noValidate
            autoComplete="off"
          >
            <TextField
              id="email"
              type="email"
              {...register('email')}
              label="Email"
              variant="outlined"
            />
            <p className="text-red-500">{errors.email?.message}</p>
            <TextField
              id="password"
              type="password"
              {...register('password')}
              label="Password"
              variant="outlined"
            />
            <p className="text-red-500">{errors.password?.message}</p>
            <button type="submit" className="btn-primary w-full">
              Sign in
            </button>
          </Box>
        </div>
      </div>
    </div>
  );
};
